import { useState } from 'react'
import type { WordDiscoveryProps } from '../../../types/word-discovery'
import type { VoteType } from '../../../types/alternatives-voting'
import { WordCard } from './WordCard'
import { WordDetailModal } from './WordDetailModal'
import { FilterBar } from './FilterBar'

/**
 * Word Discovery - Main browsing interface
 * Design tokens: amber (primary), rose (secondary), slate (neutral)
 * Typography: Cairo font family
 */
export function WordDiscovery({
  foreignWords,
  categories,
  users,
  currentUser,
  userVotes,
  selectedCategoryId = null,
  sortBy = 'popularity',
  onFilterCategory,
  onSort,
  onWordClick,
  onVote,
  onProposeAlternative,
  onShare,
}: WordDiscoveryProps) {
  const [selectedWordId, setSelectedWordId] = useState<string | null>(null)

  const selectedWord = foreignWords.find((w) => w.id === selectedWordId)

  const handleWordClick = (wordId: string) => {
    setSelectedWordId(wordId)
    onWordClick?.(wordId)
  }

  const handleCloseModal = () => {
    setSelectedWordId(null)
  }

  const handleVote = (alternativeId: string, voteType: VoteType) => {
    onVote?.(alternativeId, voteType)
  }

  const handleProposeAlternative = () => {
    if (selectedWordId) {
      onProposeAlternative?.(selectedWordId)
    }
  }

  const handleShare = () => {
    if (selectedWordId) {
      onShare?.(selectedWordId)
    }
  }

  // Get user lookup for displaying names
  // getUserById is no longer needed - user data is already in the word objects

  return (
    <div className='min-h-screen bg-slate-950'>
      {/* Hero Section */}
      <div className='relative overflow-hidden border-b border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/20'>
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.05),transparent_50%)]' />
        <div className='relative px-6 py-12 mx-auto max-w-7xl'>
          <h1 className='mb-3 text-4xl font-bold md:text-5xl text-amber-500'>
           يلا بيـــنا
          </h1>
          <p className='max-w-2xl text-lg text-slate-300'>
            دور
              على الكلمات الأجنبية وشوف البدائل المصري. صوّت على اللي
            تحبه واقترح كلمات جديدة.
          </p>

          {/* Stats */}
          <div className='flex flex-wrap gap-6 mt-8'>
            <div className='flex items-baseline gap-2'>
              <span className='text-3xl font-bold text-amber-500'>
                {foreignWords.length}
              </span>
              <span className='text-slate-400'>كلمة</span>
            </div>
            <div className='flex items-baseline gap-2'>
              <span className='text-3xl font-bold text-rose-500'>
                {foreignWords.reduce((sum, w) => sum + w.alternativesCount, 0)}
              </span>
              <span className='text-slate-400'>بديل مقترح</span>
            </div>
            <div className='flex items-baseline gap-2'>
              <span className='text-3xl font-bold text-amber-500'>
                {foreignWords.reduce((sum, w) => sum + w.totalVotes, 0)}
              </span>
              <span className='text-slate-400'>صوت</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className='sticky z-30 border-b top-16 bg-slate-900/95 backdrop-blur-sm border-slate-800'>
        <FilterBar
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          sortBy={sortBy}
          onFilterCategory={onFilterCategory}
          onSort={onSort}
        />
      </div>

      {/* Word Grid */}
      <div className='px-6 py-8 mx-auto max-w-7xl'>
        {foreignWords.length === 0 ? (
          // Empty State
          <div className='flex flex-col items-center justify-center py-20'>
            <div className='flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-slate-800'>
              <svg
                className='w-8 h-8 text-slate-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                />
              </svg>
            </div>
            <h3 className='mb-2 text-xl font-semibold text-slate-300'>
              مفيش نتائج
            </h3>
            <p className='max-w-md text-center text-slate-500'>
              جرب تغيير الفلتر أو ابحث عن كلمة تانية.
            </p>
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
            {foreignWords.map((word) => (
              <WordCard
                key={word.id}
                word={word}
                onClick={() => handleWordClick(word.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedWord && (
        <WordDetailModal
          word={selectedWord}
          users={users}
          currentUser={currentUser}
          userVotes={userVotes}
          onClose={handleCloseModal}
          onVote={handleVote}
          onProposeAlternative={handleProposeAlternative}
          onShare={handleShare}
        />
      )}
    </div>
  );
}
